import { getLogger } from "@expert/logging";
import { Button, Divider, Menu, Text } from "@mantine/core";
import { withClickAnalytics } from "@soluto-private/eventualize-react";
import { type JSX, useMemo, useState } from "react";
import { CaretDown, CaretUp, CheckMark, CloseX, DoubleCheckMark } from "@/common-ui";
import { useCustomerProgram, useVerifiedCustomerStore } from "@/customer-info";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAgentSdk, useCallbackState, useSessionId, type VoiceTask } from "@/sdk";
import { useSessionSummaryStore } from "@/session-summary/store/useSessionSummaryStore";
import type { SummaryResolution } from "@/shared-types";
import { useAsyncLoading } from "@/shared-utils";
import { handleEndCall } from "../../eventHandlers";
import { useControlsStore, withControlsLock } from "../controls.store";
import {
    buildAutomaticSurveyData,
    selectProgramName,
    selectSurveyId,
    shouldSendSurveyData,
    shouldTransferToSurvey,
} from "./survey-utils";

const AnalyticButton = withClickAnalytics(Button<"button">, "Click");
const AnalyticMenuItem = withClickAnalytics(Menu.Item<"button">, "Click");
const logger = getLogger({ module: "EndCallButton" });

/**
 * Button used to hangup a call for everyone
 *
 * in certain situations a dropdown menu is shown to allow the agent to select a resolution
 */
export function EndCallButton({ task }: { task: VoiceTask }): JSX.Element | null {
    const agentSdk = useAgentSdk();
    const callbackState = useCallbackState();
    const { controlLock, isConference, conferencePanelOpen } = useControlsStore();
    const [opened, setOpened] = useState(false);
    const {
        survey,
        features: { isEndCallResolutionMenuEnabled = true },
    } = usePartnerConfig() ?? {};
    const { surveyEnabled: partnerSurveyEnabled } = usePartnerFeatureConfigured();
    const useCustomerAgreementProgramName = survey?.useCustomerAgreementProgram ?? false;
    const excludedPrograms = survey?.excludedPrograms ?? [];
    const shouldSurveyNotResolved = survey?.shouldSurveyNotResolved ?? false;

    const sessionId = useSessionId();
    const verifiedCustomer = useVerifiedCustomerStore().getVerifiedCustomer(sessionId);
    const customerAgreementProgramName = verifiedCustomer?.customer?.productName;
    const { customerProgram } = useCustomerProgram();

    const isClaimEligibleProgram = customerProgram?.claimEligible ?? false;
    const isPartnerClaimEligible = survey?.isClaimsEligible ?? false;
    const isClaimEligible = isClaimEligibleProgram || isPartnerClaimEligible;

    const surveyableResolutionStatuses: SummaryResolution[] = useMemo(() => {
        const baseStatuses: SummaryResolution[] = ["resolved"];

        if (shouldSurveyNotResolved) {
            baseStatuses.push("not resolved");
        }

        return baseStatuses;
    }, [shouldSurveyNotResolved]);

    const anyParticipantOnHold = useMemo(() => {
        const conferenceParticipantIsOnHold =
            task.conferenceParticipants.find((participant) => participant.type === "active" && participant.hold) !==
            undefined;
        const primaryParticipantIsOnHold = task.holdState.onHold;

        return conferenceParticipantIsOnHold || primaryParticipantIsOnHold;
    }, [task.conferenceParticipants, task.holdState]);

    const isSurveyEligible = (resolutionStatus: SummaryResolution) =>
        partnerSurveyEnabled
            ? shouldTransferToSurvey({
                  customerLeftTheConference: task.customerLeftTheConference,
                  callbackState,
                  isConference,
                  conferencePanelOpen: conferencePanelOpen,
                  resolutionStatus,
                  surveyableResolutionStatuses: surveyableResolutionStatuses,
              })
            : false;

    const { setResolved } = useSessionSummaryStore();

    /**
     * Menu resolution shouldn't be shown when a callback is scheduled
     * AND the specific partner has configured it to not show
     */
    const shouldShowResolutionMenu = isEndCallResolutionMenuEnabled && !callbackState;

    const [isLoading, resolveAndEndCall] = useAsyncLoading(
        (
            resolutionStatus: SummaryResolution = "not resolved",
            hideResolutionStatusForm = false,
            claimFiled = false,
        ) => {
            return withControlsLock(async () => {
                if (resolutionStatus && shouldShowResolutionMenu) {
                    setResolved(resolutionStatus, { hideResolutionStatusForm });
                }
                const shouldSendSurvey = shouldSendSurveyData({
                    customerLeftTheConference: task.customerLeftTheConference,
                    callbackState,
                    isConference,
                    conferencePanelOpen,
                });

                if (agentSdk.areSurveysAutomatic(task) && shouldSendSurvey) {
                    await agentSdk.sendToSurvey(
                        task,
                        buildAutomaticSurveyData({
                            shouldShowResolutionMenu,
                            resolutionStatus,
                            isClaimEligible,
                            claimFiled,
                        }),
                    );
                    return;
                }

                const programName = selectProgramName(
                    useCustomerAgreementProgramName,
                    claimFiled,
                    customerProgram?.name,
                    customerAgreementProgramName,
                    survey?.ignoreProgram ?? false,
                    excludedPrograms,
                );

                const surveyId = selectSurveyId(
                    useCustomerAgreementProgramName,
                    claimFiled,
                    customerProgram,
                    survey,
                    resolutionStatus,
                );

                const shouldSendToSurvey =
                    isSurveyEligible(resolutionStatus) &&
                    (customerProgram || useCustomerAgreementProgramName || survey?.ignoreProgram) &&
                    programName &&
                    surveyId;

                if (shouldSendToSurvey) {
                    await agentSdk.sendToSurvey(task, { program: programName, surveyId });
                } else {
                    await handleEndCall(agentSdk, task);
                }
            })();
        },
    );

    const noAcceptedCall = !task.conferenceId || !task.conferenceStarted;

    if (noAcceptedCall) {
        // ? redundant check. consider removing and trusting the developer to use this component correctly
        logger.error(task, "EndCall button should never be displayed for a call that has not been accepted.");
    }

    return shouldShowResolutionMenu ? (
        <Menu position="bottom-end" opened={opened} shadow="md" onChange={setOpened}>
            <Menu.Target>
                <AnalyticButton
                    analytics={{ actionId: "HangupButton_MenuDropdownToggle" }}
                    aria-label="End Call"
                    data-testid="call-controls-hangup-menu"
                    disabled={isLoading || task.status !== "assigned" || controlLock || anyParticipantOnHold}
                    loading={isLoading || controlLock}
                    radius="xl"
                    rightSection={
                        opened ? (
                            <CaretUp fill="var(--mantine-color-white)" />
                        ) : (
                            <CaretDown fill="var(--mantine-color-white)" />
                        )
                    }
                    size="xs"
                    color={opened ? "red.8" : "red.9"}
                >
                    <Text size="xs">End call</Text>
                </AnalyticButton>
            </Menu.Target>
            <Menu.Dropdown bd="none" bg="dark.7">
                <AnalyticMenuItem
                    analytics={{ actionId: "HangupButton_IssueResolved" }}
                    data-testid="call-controls-hangup-menu-resolved"
                    py="xs"
                    onClick={() => void resolveAndEndCall("resolved", true)}
                    leftSection={<CheckMark width={18} fill="var(--mantine-color-dark-2)" />}
                >
                    <Text size="xs">Issue resolved</Text>
                </AnalyticMenuItem>
                {isClaimEligible && (
                    <>
                        <AnalyticMenuItem
                            analytics={{ actionId: "HangupButton_IssueResolvedClaimFiled" }}
                            data-testid="call-controls-hangup-menu-claim-filed"
                            py="xs"
                            onClick={() => void resolveAndEndCall("resolved", true, true)}
                            leftSection={<DoubleCheckMark width={18} fill="var(--mantine-color-dark-2)" />}
                        >
                            <Text size="sm">Resolved, claim filed</Text>
                        </AnalyticMenuItem>
                        <Divider color="dark.4" mt={5} mb={5} />
                    </>
                )}

                <AnalyticMenuItem
                    analytics={{ actionId: "HangupButton_IssueNotResolved" }}
                    data-testid="call-controls-hangup-menu-not-resolved"
                    py="xs"
                    onClick={() => void resolveAndEndCall("not resolved", true)}
                    leftSection={<CloseX height={18} fill="var(--mantine-color-dark-2)" />}
                >
                    <Text size="xs">Issue not resolved</Text>
                </AnalyticMenuItem>
                <AnalyticMenuItem
                    analytics={{ actionId: "HangupButton_Transferred" }}
                    data-testid="call-controls-hangup-menu-transferred"
                    py="xs"
                    onClick={() => void resolveAndEndCall("transferred", true)}
                    leftSection={<CloseX height={18} fill="var(--mantine-color-dark-2)" />}
                >
                    <Text size="xs">Transferred</Text>
                </AnalyticMenuItem>
            </Menu.Dropdown>
        </Menu>
    ) : (
        <AnalyticButton
            analytics={{ actionId: "HangupButton" }}
            aria-label="End Call"
            data-testid="call-controls-hangup"
            disabled={isLoading || task.status !== "assigned" || controlLock || anyParticipantOnHold}
            loading={isLoading || controlLock}
            onClick={() => void resolveAndEndCall("not resolved")}
            radius="xl"
            size="xs"
            color="red.9"
        >
            <Text size="xs">End call</Text>
        </AnalyticButton>
    );
}
